import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button } from '../catalyst/button'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../catalyst/dialog'
import { Field as FormField, FieldGroup, Label } from '../catalyst/fieldset'
import { Subheading } from '../catalyst/heading'
import { Input } from '../catalyst/input'
import { Text } from '../catalyst/text'
import { useAuth } from '../lib/auth'
import { issueFor, useCanadaGapFarmStatus, useEntryCheck, type CanadaGapCheckResult } from '../lib/canadagap'
import { ComplianceBadge } from '../lib/compliance-badge'
import { usePestMonitoringChecks, type PestMonitoringCheckInput } from '../lib/farm-level-logs'
import { FarmLogLayout, orNull, text, useSavedEntries, type DialogState, type TimelineEntry } from '../lib/farm-log-layout'
import { useLogNames } from '../lib/farm-log-names'
import { AssetNameInput } from '../lib/asset-picker'
import { assetIdFor, useFarmAssets } from '../lib/inventory'
import type { Asset, PestMonitoringCheck } from '../lib/types'
import { checkBadgeProps, EntryCheckPanel, InputWarning } from './entry-check'
import { EntryIssues } from './entry-issues'

const FORM_NAME = 'Pest Monitoring for Buildings'

type TrapRow = { deviceOrArea: string; findings: string; actionTaken: string }
const emptyRow = (deviceOrArea = ''): TrapRow => ({ deviceOrArea, findings: '', actionTaken: '' })

function DialogFrame({ title, description, onClose, onSubmit, submitting, error, check, footer, children }: {
  title: string
  description?: string
  onClose: () => void
  onSubmit: (event: React.FormEvent) => void
  submitting: boolean
  error: string | null
  /** The live CanadaGAP check of what is being recorded, for the badges. */
  check: CanadaGapCheckResult | null
  /** Below the fields, above Save (the CanadaGAP check panel). */
  footer?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Dialog open onClose={onClose} size="3xl">
      <DialogTitle>{title}</DialogTitle>
      {description && <DialogDescription>{description}</DialogDescription>}
      <form onSubmit={onSubmit}>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <ComplianceBadge framework="CanadaGAP" formCode="M" formName={FORM_NAME} {...checkBadgeProps(check)} />
        </div>
        <DialogBody>
          {error && <Text className="mb-4 text-red-600 dark:text-red-400">{error}</Text>}
          {children}
          {footer}
        </DialogBody>
        <DialogActions>
          <div className="sm:mr-auto">
            <ComplianceBadge framework="CanadaGAP" formCode="M" formName={FORM_NAME} placement="above" {...checkBadgeProps(check)} />
          </div>
          <Button plain onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

/** A round row's CanadaGAP warning under its Findings input: each row is saved as its own Form M entry. */
function TrapFindingsWarning({
  orgId,
  farmId,
  entry,
  onResult,
}: {
  orgId: string | undefined
  farmId: string | undefined
  entry: Record<string, unknown>
  /** Reports this row's check to the round, for the badges; null when the row goes away. */
  onResult: (result: CanadaGapCheckResult | null) => void
}) {
  const check = useEntryCheck(orgId, farmId, { form: 'm', entry })
  useEffect(() => {
    onResult(check)
  }, [check, onResult])
  useEffect(() => () => onResult(null), [onResult])
  return <InputWarning result={check} input="findings" />
}

/** The round's badge: every checked row's gaps and violations together. */
function mergeChecks(results: (CanadaGapCheckResult | null)[]): CanadaGapCheckResult | null {
  const known = results.filter((r) => r !== null)
  if (known.length === 0) return null
  return {
    missing: [...new Set(known.flatMap((r) => r.missing))],
    violations: known.flatMap((r) => r.violations),
    inputs: known.flatMap((r) => r.inputs),
  }
}

/** A monthly round of one building: every trap or controlled area checked, one row each. */
function PestRoundDialog({
  orgId,
  farmId,
  onClose,
  buildings,
  assets,
  onSave,
}: {
  orgId: string | undefined
  farmId: string | undefined
  onClose: () => void
  buildings: string[]
  /** Inventory buildings at this farm. */
  assets: Asset[]
  onSave: (inputs: PestMonitoringCheckInput[]) => Promise<unknown>
}) {
  const [buildingName, setBuildingName] = useState('')
  const [checkedOn, setCheckedOn] = useState('')
  const [personResponsible, setPersonResponsible] = useState('')
  const [rows, setRows] = useState<TrapRow[]>([emptyRow()])
  const [touched, setTouched] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Until a finding is typed, list the building's known traps (the same numbers as on Form A). Only
  // a building already on file is looked up, not every keystroke of a new name.
  const known = buildings.includes(buildingName.trim())
  const assetId = assetIdFor(assets, buildingName)
  const { names: devices } = useLogNames(orgId, farmId, known ? 'pest_device' : undefined, known ? buildingName.trim() : undefined)
  useEffect(() => {
    if (!touched) setRows(known && devices.length > 0 ? devices.map((d) => emptyRow(d)) : [emptyRow()])
  }, [devices, known, touched])

  // Person responsible is shared by every row, so it is checked once; findings are checked per row.
  const roundCheck = useEntryCheck(orgId, farmId, { form: 'm', entry: { personResponsible: orNull(personResponsible) } })
  const anyNamed = rows.some((r) => r.deviceOrArea.trim())
  // Each checked row's result, by row position.
  const [rowChecks, setRowChecks] = useState<Record<number, CanadaGapCheckResult | null>>({})
  const reporters = useMemo(
    () => rows.map((_, index) => (result: CanadaGapCheckResult | null) => setRowChecks((prev) => ({ ...prev, [index]: result }))),
    [rows.length],
  )
  const roundBadgeCheck = mergeChecks(Object.values(rowChecks))

  const update = (index: number, key: keyof TrapRow, value: string) => {
    setTouched(true)
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)))
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const filled = rows.filter((r) => r.deviceOrArea.trim())
    if (!buildingName.trim() || !checkedOn || filled.length === 0) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave(
        filled.map((r) => ({
          buildingName: buildingName.trim(),
          assetId,
          checkedOn,
          deviceOrArea: r.deviceOrArea.trim(),
          findings: orNull(r.findings),
          actionTaken: orNull(r.actionTaken),
          personResponsible: orNull(personResponsible),
        })),
      )
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save pest checks.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <DialogFrame
      title="Record a pest monitoring round"
      description="At least monthly while in use. Record every trap or controlled area, with what was found and any action taken."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitting={submitting}
      error={error}
      check={roundBadgeCheck}
    >
      <FieldGroup>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormField>
            <Label>Building ID # / name</Label>
            <AssetNameInput
              assets={assets}
              pastNames={buildings}
              linkedAssetId={assetId}
              inventoryHref={`/farms/${farmId}/inventory`}
              value={buildingName}
              onChange={setBuildingName}
              placeholder="e.g. Packing shed"
              required
              autoFocus
            />
          </FormField>
          <FormField>
            <Label>Date</Label>
            <Input type="date" value={checkedOn} onChange={(e) => setCheckedOn(e.target.value)} required />
          </FormField>
          <FormField>
            <Label>Person responsible</Label>
            <Input value={personResponsible} onChange={(e) => setPersonResponsible(e.target.value)} />
            <InputWarning result={roundCheck} input="personResponsible" />
          </FormField>
        </div>
      </FieldGroup>

      <Subheading className="mt-8">Traps and areas</Subheading>
      <div className="mt-3 space-y-3">
        {rows.map((row, index) => (
          <div key={index} className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[9rem_1fr_1fr_auto]">
            <FormField>
              {index === 0 && <Label>Device # or area</Label>}
              <Input value={row.deviceOrArea} onChange={(e) => update(index, 'deviceOrArea', e.target.value)} placeholder="e.g. Trap 4" aria-label="Device number or area" />
            </FormField>
            <FormField>
              {index === 0 && <Label>Findings</Label>}
              <Input value={row.findings} onChange={(e) => update(index, 'findings', e.target.value)} placeholder="e.g. No activity" aria-label="Findings" />
              {(row.deviceOrArea.trim() || !anyNamed) && (
                <TrapFindingsWarning
                  orgId={orgId}
                  farmId={farmId}
                  onResult={reporters[index]!}
                  entry={{
                    buildingName: orNull(buildingName),
                    checkedOn: checkedOn || null,
                    deviceOrArea: orNull(row.deviceOrArea),
                    findings: orNull(row.findings),
                    actionTaken: orNull(row.actionTaken),
                    personResponsible: orNull(personResponsible),
                  }}
                />
              )}
            </FormField>
            <FormField>
              {index === 0 && <Label>Action taken</Label>}
              <Input value={row.actionTaken} onChange={(e) => update(index, 'actionTaken', e.target.value)} placeholder="If any" aria-label="Action taken" />
            </FormField>
            <Button
              plain
              disabled={rows.length === 1}
              onClick={() => {
                setTouched(true)
                setRows((prev) => prev.filter((_, i) => i !== index))
              }}
            >
              Remove
            </Button>
          </div>
        ))}
      </div>
      <Button
        plain
        className="mt-3"
        onClick={() => {
          setTouched(true)
          setRows((prev) => [...prev, emptyRow()])
        }}
      >
        + Add trap or area
      </Button>
    </DialogFrame>
  )
}

/** Editing one trap's row from an earlier round. */
function PestCheckDialog({
  onClose,
  initial,
  buildings,
  assets,
  onSave,
}: {
  onClose: () => void
  initial: PestMonitoringCheck
  buildings: string[]
  assets: Asset[]
  onSave: (input: PestMonitoringCheckInput) => Promise<unknown>
}) {
  const [buildingName, setBuildingName] = useState(initial.buildingName)
  const [checkedOn, setCheckedOn] = useState(initial.checkedOn)
  const [deviceOrArea, setDeviceOrArea] = useState(initial.deviceOrArea)
  const [findings, setFindings] = useState(text(initial.findings))
  const [actionTaken, setActionTaken] = useState(text(initial.actionTaken))
  const [personResponsible, setPersonResponsible] = useState(text(initial.personResponsible))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { org } = useAuth()
  const { farmId } = useParams<{ farmId: string }>()
  const assetId = assetIdFor(assets, buildingName, { name: initial.buildingName, assetId: initial.assetId })
  const check = useEntryCheck(org?.id, farmId, {
    form: 'm',
    entry: {
      buildingName: orNull(buildingName),
      checkedOn: checkedOn || null,
      deviceOrArea: orNull(deviceOrArea),
      findings: orNull(findings),
      actionTaken: orNull(actionTaken),
      personResponsible: orNull(personResponsible),
    },
  })

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!buildingName.trim() || !checkedOn || !deviceOrArea.trim()) return
    setSubmitting(true)
    setError(null)
    try {
      await onSave({
        buildingName: buildingName.trim(),
        assetId,
        checkedOn,
        deviceOrArea: deviceOrArea.trim(),
        findings: orNull(findings),
        actionTaken: orNull(actionTaken),
        personResponsible: orNull(personResponsible),
      })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save pest check.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <DialogFrame
      title="Edit pest check"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitting={submitting}
      error={error}
      check={check}
      footer={<EntryCheckPanel result={check} formCode="M" />}
    >
      <FieldGroup>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormField>
            <Label>Building ID # / name</Label>
            <AssetNameInput
              assets={assets}
              pastNames={buildings}
              linkedAssetId={assetId}
              inventoryHref={`/farms/${farmId}/inventory`}
              value={buildingName}
              onChange={setBuildingName}
              required
            />
          </FormField>
          <FormField>
            <Label>Date</Label>
            <Input type="date" value={checkedOn} onChange={(e) => setCheckedOn(e.target.value)} required />
          </FormField>
          <FormField>
            <Label>Device # or area</Label>
            <Input value={deviceOrArea} onChange={(e) => setDeviceOrArea(e.target.value)} required />
          </FormField>
        </div>
        <FormField>
          <Label>Findings</Label>
          <Input value={findings} onChange={(e) => setFindings(e.target.value)} />
          <InputWarning result={check} input="findings" />
        </FormField>
        <FormField>
          <Label>Action taken</Label>
          <Input value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} placeholder="If any" />
        </FormField>
        <FormField>
          <Label>Person responsible</Label>
          <Input value={personResponsible} onChange={(e) => setPersonResponsible(e.target.value)} />
          <InputWarning result={check} input="personResponsible" />
        </FormField>
      </FieldGroup>
    </DialogFrame>
  )
}

export function PestMonitoringPage() {
  const { farmId } = useParams<{ farmId: string }>()
  const { org } = useAuth()
  const [year, setYear] = useState(new Date().getFullYear())
  const [building, setBuilding] = useState('')
  const [dialog, setDialog] = useState<DialogState<PestMonitoringCheck>>(null)
  const saved = useSavedEntries(dialog !== null, year)

  const { logs, loading, error, addLogs, updateLog } = usePestMonitoringChecks(org?.id, farmId, year, building || undefined)
  const { status, reload: reloadStatus } = useCanadaGapFarmStatus(org?.id, farmId, year)
  const { names, reload: reloadNames } = useLogNames(org?.id, farmId, 'building')
  const { assets } = useFarmAssets(org?.id, farmId, 'BUILDING')

  async function afterSave() {
    await Promise.all([reloadStatus(), reloadNames()])
  }

  // One timeline item per round: a building checked on a date.
  const rounds = new Map<string, PestMonitoringCheck[]>()
  for (const log of logs) {
    const key = `${log.checkedOn}|${log.buildingName}`
    rounds.set(key, [...(rounds.get(key) ?? []), log])
  }
  const entries: TimelineEntry[] = [...rounds]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([key, checks]) => {
      const sorted = [...checks].sort((a, b) => a.deviceOrArea.localeCompare(b.deviceOrArea, undefined, { numeric: true }))
      const person = sorted.find((c) => c.personResponsible)?.personResponsible
      return {
        key,
        date: sorted[0]!.checkedOn,
        content: (
          <>
            <p className="mt-0.5 text-sm text-zinc-700 dark:text-zinc-300">
              {sorted[0]!.buildingName}
              {person && <span className="text-zinc-400 dark:text-zinc-500"> · {person}</span>}
            </p>
            <ul className="mt-2 space-y-1">
              {sorted.map((check) => (
                <li key={check.id}>
                  <button
                    type="button"
                    onClick={() => setDialog({ log: check })}
                    className="-mx-1 rounded px-1 text-left text-sm text-zinc-700 hover:bg-zinc-950/5 dark:text-zinc-300 dark:hover:bg-white/5"
                  >
                    <span className="font-medium">{check.deviceOrArea}</span>
                    <span className="text-zinc-500 dark:text-zinc-400">
                      {' '}
                      — {check.findings ?? 'no findings recorded'}
                      {check.actionTaken && <> · {check.actionTaken}</>}
                    </span>
                  </button>
                  <EntryIssues issue={issueFor(status?.forms.m, check.id)} />
                </li>
              ))}
            </ul>
          </>
        ),
      }
    })

  return (
    <FarmLogLayout
      orgId={org?.id}
      farmId={farmId}
      form="m"
      title="Pest monitoring"
      formName={FORM_NAME}
      addLabel="Record a round"
      addHref={`/farms/${farmId}/log/pest_check`}
      year={year}
      onYearChange={setYear}
      nameFilter={{ label: 'Building', options: names, value: building, onChange: setBuilding }}
      status={status}
      loading={loading}
      error={error}
      entries={entries}
      emptyText={`No pest monitoring in ${year}.`}
      saved={saved}
    >
      {dialog?.log ? (
        <PestCheckDialog
          key={dialog.log.id}
          onClose={() => setDialog(null)}
          initial={dialog.log}
          buildings={names}
          assets={assets}
          onSave={async (input) => {
            const log = await updateLog(dialog.log!.id, input)
            await afterSave()
            if (log) saved.setIds([log.id])
          }}
        />
      ) : (
        dialog && (
          <PestRoundDialog
            orgId={org?.id}
            farmId={farmId}
            onClose={() => setDialog(null)}
            buildings={names}
            assets={assets}
            onSave={async (inputs) => {
              const created = await addLogs(inputs)
              await afterSave()
              if (created) saved.setIds(created.map((c) => c.id))
            }}
          />
        )
      )}
    </FarmLogLayout>
  )
}
